<script setup>
// TODO: DO WE NEED TO KEEP THIS FILE??? - same as IdentificationScreen.vue
import {Gender, GenderLabel} from "@/choices";

import {computed, watch} from "vue";
import HpoPatientModalSection from "../HpoPatientModalSection.vue";
import {numberValidator, stringValidator} from "@/utils/yupValidators";
import * as yup from "yup";
import {HpoPatientIdentificationMutation} from "@/graphql/mutations/hpo/patient/identification.graphql";
import {useMutation} from "@vue/apollo-composable";
import {requiredMessage} from "../../../../utils/yupValidators";
import dayjs from "dayjs";
import {isStandardBirthNumber} from "./identification/utils";
import {gettext, interpolate} from "../../../../utils/translate";
import {PoliceVerification, PoliceVerificationButtonLabel} from "../../choices";
import VuetifyErrorMessage from "../../../forms/VuetifyErrorMessage.vue";
import {mdiGenderFemale, mdiGenderMale, mdiHuman, mdiHumanChild} from "@mdi/js";
import {useDjangoForm} from "@/utils/forms";


const now = dayjs()
const monthMessage = gettext('Month must be between 1 and 12')
const yearMessage = interpolate(gettext('Year can not be greater than %(thisYear)s'), {
    thisYear: now.year()
})


const props = defineProps({
    hpoId: {
        type: String,
        required: true,
    },
    patientData: {
        type: Object,
        required: true,
    },
    countries: {
        type: Array,
        required: true,
    },
    member: {
        type: Object,
        required: true
    }
})
const emit = defineEmits(['close'])

const countryOptions = props.countries.filter((item) => ['CZ', 'DE', 'SK', 'UA'].includes(item.code))

const insuranceChoices = [
    {
        "code": "111",
        "name": "111 - Všeobecná zdravotní pojišťovna"
    },
    {
        "code": "201",
        "name": "201 - Vojenská zdravotní pojišťovna"
    },
    {
        "code": "205",
        "name": "205 - Česká průmyslová zdravotní pojišťovna"
    },
    {
        "code": "207",
        "name": "207 - Oborová zdravotní pojišťovna"
    },
    {
        "code": "209",
        "name": "209 - Zaměstnanecká pojišťovna Škoda"
    },
    {
        "code": "211",
        "name": "211 - Zdravotní pojišťovna ministerstva vnitra"
    },
    {
        "code": "213",
        "name": "213 - Revírní bratrská pokladna"
    }
]


const schema = yup.object({
    country: stringValidator.required(requiredMessage),
    gender: stringValidator.required(requiredMessage),
    isChild: yup.boolean().required(requiredMessage),
    firstName: stringValidator.required(requiredMessage),
    lastName: stringValidator.required(requiredMessage),
    // This tab posts the same HpoPatientIdentificationMutation as the wizard, so the birth
    // parts must be required here too — the backend IdentificationForm now rejects them as
    // missing regardless of which UI submitted. Keep in step with identification/schema.js.
    birthDay: numberValidator
        .required(requiredMessage)
        .min(1, gettext('Date too low'))
        .test({
            name: 'maxDate',
            exclusive: false,
            params: {},
            message: gettext('Date too large'),
            test: function (value) {
                if (value && this.parent.birthMonth && this.parent.birthYear) {
                    const d = dayjs(this.parent.birthYear + '-' + this.parent.birthMonth + '-01')
                    return parseInt(value) <= d.daysInMonth()
                }
                return true
            }
        }),
    birthMonth: numberValidator.required(requiredMessage).min(1, monthMessage).max(12, monthMessage),
    birthYear: numberValidator.required(requiredMessage).max(now.year(), yearMessage),
    phoneNumber: numberValidator,
    note: stringValidator,
    policeVerification: yup.number().oneOf(Object.values(PoliceVerification)),
    nonStandardBirthNumber: yup.boolean().nullable(),
    birthNumber: stringValidator
        .transform((value) => value ? value : null)
        .matches(/^\d+$/, gettext("Birth number can contain only digits"))
        .when('nonStandardBirthNumber', {
            is: true,
            then: () => {
                return stringValidator
            },
            otherwise: () => {
                return stringValidator.test(
                    'standard-birth-number-test',
                    gettext("Invalid birth number"),
                    (value, context) => {
                        if (value) {
                            return isStandardBirthNumber(value)
                        }
                        return true
                    })
            }
        }),
    insurance: stringValidator,
    address: stringValidator,
    city: stringValidator.when('address', {
        is: (val) => {
            return !!val;
        },
        then: () => {
            return stringValidator.required(requiredMessage)
        },
        otherwise: () => {
            return stringValidator
        }
    }),
});

const {mutate, loading} = useMutation(HpoPatientIdentificationMutation)

const {defineField, handleMutationSubmit} = useDjangoForm({
    validationSchema: schema,
    initialValues: props.patientData.patient ? {
        country: props.patientData.patient.country,
        gender: props.patientData.patient.gender,
        isChild: props.patientData.patient.isChild,
        firstName: props.patientData.patient.firstName,
        lastName: props.patientData.patient.lastName,
        birthDay: props.patientData.patient.birthDay,
        birthMonth: props.patientData.patient.birthMonth,
        birthYear: props.patientData.patient.birthYear,
        birthNumber: props.patientData.patient.birthNumber,
        nonStandardBirthNumber: props.patientData.patient.nonStandardBirthNumber,
        insurance: props.patientData.patient.insurance,
        address: props.patientData.patient.address,
        city: props.patientData.patient.city,
        phoneNumber: props.patientData.patient.phoneNumber,
        note: props.patientData.patient.note,
        policeVerification: props.patientData.patient.policeVerification
    } : null
});

const [country, countryProps] = defineField('country');
const [gender, genderProps] = defineField('gender');
const [isChild, isChildProps] = defineField('isChild');
const [firstName, firstNameProps] = defineField('firstName');
const [lastName, lastNameProps] = defineField('lastName');
const [birthDay, birthDayProps] = defineField('birthDay');
const [birthMonth, birthMonthProps] = defineField('birthMonth');
const [birthYear, birthYearProps] = defineField('birthYear');
const [birthNumber, birthNumberProps] = defineField('birthNumber');
const [nonStandardBirthNumber, nonStandardBirthNumberProps] = defineField('nonStandardBirthNumber');
const [insurance, insuranceProps] = defineField('insurance');
const [address, addressProps] = defineField('address');
const [city, cityProps] = defineField('city');
const [phoneNumber, phoneNumberProps] = defineField('phoneNumber');
const [note, noteProps] = defineField('note');
const [policeVerification, policeVerificationProps] = defineField('policeVerification');

watch(insurance, (newVal) => {
    if (newVal && typeof newVal === 'object') {
        insurance.value = newVal.code;
    }
});

const onSubmit = handleMutationSubmit(mutate, {
    buildValues(values) {
        values.hpoId = props.hpoId
        try {
            values.personId = props.patientData.patient.id
        } catch (e) {
            values.personId = null
        }
    },
    onSuccess() {
        emit('close');
    }
});

const birthDateIsValid = computed(() => {
    if (birthDay.value && birthMonth.value && birthYear.value) {
        return dayjs(new Date(birthYear.value, birthMonth.value - 1, birthDay.value)) <= now
    }
    return true
})


const identificationComplete = computed(() => {
    return firstName.value && lastName.value && birthDay.value && birthMonth.value && birthYear.value
})

</script>

<template>
    <div class="tab-body">
        <HpoPatientModalSection :title="pgettext('label', 'Identification')">
            <template v-slot:body>
                <v-form @submit.prevent="onSubmit">
                    <v-card :title="pgettext('label', 'Country')">
                        <v-card-text>
                            <v-row>
                                <v-col cols="12" md="7" lg="6">
                                    <v-tabs v-model="country" align-tabs="center" density="comfortable"
                                            :mandatory="false" grow>
                                        <v-tab v-for="country in countryOptions" :value="country.code"
                                               variant="elevated">
                                            {{ country.name }}
                                        </v-tab>
                                    </v-tabs>
                                    <VuetifyErrorMessage v-bind="countryProps"/>
                                </v-col>


                                <v-col cols="12" md="5" lg="6">
                                    <v-autocomplete v-model="country" v-bind="countryProps"
                                                    :label="pgettext('label', 'Country')"
                                                    :items="countries"
                                                    item-value="code"
                                                    item-title="name"
                                    ></v-autocomplete>
                                </v-col>
                            </v-row>
                        </v-card-text>
                    </v-card>
                    <v-card>
                        <v-row>
                            <v-col cols="12" md="6">
                                <v-card :title="pgettext('label', 'Gender')">
                                    <v-card-text>
                                        <v-row>
                                            <v-col>
                                                <v-tabs v-model="gender" align-tabs="center" density="comfortable"
                                                        grow>
                                                    <v-tab :value="Gender.MALE" variant="elevated"
                                                           :prepend-icon="mdiGenderMale">
                                                        {{ GenderLabel[Gender.MALE] }}
                                                    </v-tab>
                                                    <v-tab :value="Gender.FEMALE" variant="elevated"
                                                           :prepend-icon="mdiGenderFemale">
                                                        {{ GenderLabel[Gender.FEMALE] }}
                                                    </v-tab>
                                                </v-tabs>
                                                <VuetifyErrorMessage v-bind="genderProps"/>
                                            </v-col>
                                        </v-row>
                                    </v-card-text>
                                </v-card>
                            </v-col>
                            <v-col cols="12" md="6">
                                <v-card :title="pgettext('label', 'Adult / child')">
                                    <v-card-text>
                                        <v-row>
                                            <v-col>
                                                <v-tabs v-model="isChild" align-tabs="center" density="comfortable"
                                                        grow>
                                                    <v-tab :value="false" variant="elevated" :prepend-icon="mdiHuman">
                                                        {{ pgettext('label', 'adult').toUpperCase() }}
                                                    </v-tab>
                                                    <v-tab :value="true" variant="elevated"
                                                           :prepend-icon="mdiHumanChild">
                                                        {{ pgettext('label', 'child').toUpperCase() }}
                                                    </v-tab>
                                                </v-tabs>
                                                <VuetifyErrorMessage v-bind="isChildProps"/>
                                            </v-col>
                                        </v-row>
                                    </v-card-text>
                                </v-card>
                            </v-col>
                        </v-row>
                    </v-card>

                    <v-card :title="pgettext('label', 'Personal data')">
                        <v-card-text>
                            <!-- NAME & INSURANCE-->
                            <v-row>
                                <v-col cols="12" md="4">
                                    <v-text-field v-model="firstName"
                                                  v-bind="firstNameProps"
                                                  autocomplete="off"
                                                  :label="pgettext('label', 'First name')"/>
                                </v-col>
                                <v-col cols="12" md="4">
                                    <v-text-field v-model="lastName"
                                                  v-bind="lastNameProps"
                                                  autocomplete="off"
                                                  :label="pgettext('label', 'Last name')"/>
                                </v-col>
                                <v-col cols="12" md="4">
                                    <v-combobox
                                        v-model="insurance"
                                        v-bind="insuranceProps"
                                        :label="pgettext('label', 'Insurance')"
                                        :items="insuranceChoices"
                                        item-title="name"
                                    ></v-combobox>
                                </v-col>
                            </v-row>
                            <!-- BIRTH DATE -->
                            <v-row>
                                <v-alert v-if="!birthDateIsValid"
                                         color="error"
                                         icon="$error"
                                         :title="gettext('Date of birth is in future')"
                                ></v-alert>
                            </v-row>
                            <v-row>
                                <v-col>
                                    <v-row>
                                        <v-col cols="4">
                                            <!-- Birth day -->
                                            <v-number-input v-model="birthDay" v-bind="birthDayProps"
                                                            :label="pgettext('label', 'Birth day')"
                                                            :min="1" :step="1"
                                                            control-variant="hidden"/>
                                        </v-col>
                                        <v-col cols="4">
                                            <!-- Birth month -->
                                            <v-number-input v-model="birthMonth" v-bind="birthMonthProps"
                                                            :label="pgettext('label', 'Birth month')"
                                                            :min="1" :step="1"
                                                            control-variant="hidden"/>
                                        </v-col>
                                        <v-col cols="4">
                                            <!-- Birth year -->
                                            <v-number-input v-model="birthYear" v-bind="birthYearProps"
                                                            :label="pgettext('label', 'Birth year')"
                                                            :min="1900" :step="1"
                                                            control-variant="hidden"/>
                                        </v-col>
                                    </v-row>
                                </v-col>
                            </v-row>

                            <!-- BIRTH NUMBER -->
                            <v-row>
                                <v-col cols="12" md="6">
                                    <v-text-field v-model="birthNumber"
                                                  v-bind="birthNumberProps"
                                                  type="number"
                                                  autocomplete="off"
                                                  :label="pgettext('label', 'Birth number')"/>
                                </v-col>
                                <v-col cols="12" md="6">
                                    <v-checkbox-btn
                                        v-if="birthNumber && !isStandardBirthNumber(birthNumber)"
                                        v-model="nonStandardBirthNumber"
                                        v-bind="nonStandardBirthNumberProps"
                                        :label="pgettext('label', 'Nonstandard birth number')"
                                    ></v-checkbox-btn>
                                </v-col>
                            </v-row>
                        </v-card-text>
                    </v-card>
                    <v-card :title="pgettext('title', 'Other information')">
                        <v-card-text>
                            <v-row>
                                <v-col cols="12" md="6">
                                    <v-text-field v-model="address"
                                                  v-bind="addressProps"
                                                  autocomplete="off"
                                                  :label="pgettext('label', 'Address')"/>
                                </v-col>
                                <v-col cols="12" md="6">
                                    <v-text-field v-model="city"
                                                  v-bind="cityProps"
                                                  autocomplete="off"
                                                  :label="pgettext('label', 'City')"/>
                                </v-col>
                            </v-row>
                            <v-row>
                                <v-col cols="12" md="6">
                                    <v-text-field v-model="phoneNumber"
                                                  v-bind="phoneNumberProps"
                                                  autocomplete="off"
                                                  :label="pgettext('label', 'Phone number')"/>
                                </v-col>
                                <v-col cols="12" md="6">
                                    <v-text-field v-model="note"
                                                  v-bind="noteProps"
                                                  autocomplete="off"
                                                  :label="pgettext('label', 'Note')"/>
                                </v-col>
                            </v-row>
                        </v-card-text>
                    </v-card>

                    <v-card :title="pgettext('title', 'Police verification')">
                        <v-card-text>
                            <v-card-subtitle v-if="!identificationComplete">
                                <strong class="text-error">
                                    ({{
                                        gettext('To enable the "Verified by the police" option please enter at least first name, last name and birth date')
                                    }})
                                </strong>
                            </v-card-subtitle>
                            <v-row>
                                <v-col>
                                    <v-radio-group v-model="policeVerification" v-bind="policeVerificationProps">
                                        <v-radio color="primary"
                                                 :value="PoliceVerification.UNVERIFIED"
                                                 :label="PoliceVerificationButtonLabel[PoliceVerification.UNVERIFIED]"
                                        ></v-radio>
                                        <v-radio color="success"
                                                 :value="PoliceVerification.VERIFIED"
                                                 :label="PoliceVerificationButtonLabel[PoliceVerification.VERIFIED]"
                                                 :disabled="!identificationComplete">
                                        </v-radio>
                                        <v-radio color="red"
                                                 :value="PoliceVerification.UNABLE"
                                                 :label="PoliceVerificationButtonLabel[PoliceVerification.UNABLE]"
                                        ></v-radio>
                                    </v-radio-group>
                                </v-col>
                            </v-row>
                        </v-card-text>
                    </v-card>

                    <v-btn :loading="loading"
                           :disabled="loading" :text="pgettext('action', 'Save')"
                           type="submit"
                           block
                    ></v-btn>
                </v-form>
            </template>
        </HpoPatientModalSection>
    </div>
</template>